<script setup>
import { ref, watch, onMounted } from 'vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const methodOptions = [
  { id: 'POST', name: 'POST' },
  { id: 'GET', name: 'GET' },
  { id: 'PUT', name: 'PUT' },
  { id: 'PATCH', name: 'PATCH' },
  { id: 'DELETE', name: 'DELETE' },
];

const method = ref(methodOptions[0]);
const url = ref('');
const headers = ref('');
const body = ref('');
const headerPlaceholder =
  '{ "Authorization": "Bearer token", "Content-Type": "application/json" }';
const bodyPlaceholder = '{ "phone": "{{ contact.phone_number }}" }';
const availableVariables = [
  '{{ contact.name }}',
  '{{ contact.phone_number }}',
  '{{ contact.email }}',
  '{{ contact.identifier }}',
  '{{ conversation.id }}',
  '{{ conversation.display_id }}',
];

onMounted(() => {
  if (!props.modelValue) return;
  try {
    const data = JSON.parse(props.modelValue);
    if (data && data.url) {
      const selectedMethod = methodOptions.find(m => m.id === data.method);
      method.value = selectedMethod || methodOptions[0];
      url.value = data.url || '';
      headers.value = data.headers || '';
      body.value = data.body || '';
    } else {
      url.value = props.modelValue;
    }
  } catch (e) {
    url.value = props.modelValue;
  }
});

const updateValue = () => {
  const payload = {
    method: method.value.id,
    url: url.value,
    headers: headers.value,
    body: body.value,
  };
  emit('update:modelValue', JSON.stringify(payload));
};

watch([method, url, headers, body], () => {
  updateValue();
});
</script>

<template>
  <div
    class="flex flex-col gap-3 p-3 mt-2 rounded-lg border border-n-weak bg-n-alpha-1 dark:bg-n-solid-2"
  >
    <div class="flex items-center gap-2 w-full">
      <div class="w-32 flex-shrink-0">
        <label class="block mb-1 text-xs font-medium text-n-slate-11">
          {{ t('AUTOMATION.ACTION.CUSTOM_WEBHOOK.METHOD') }}
        </label>
        <SingleSelect
          v-model="method"
          :options="methodOptions"
          disable-deselect
        />
      </div>
      <NextInput
        v-model="url"
        type="url"
        class="flex-grow"
        :label="t('AUTOMATION.ACTION.CUSTOM_WEBHOOK.URL')"
        :placeholder="t('AUTOMATION.ACTION.CUSTOM_WEBHOOK.URL_PLACEHOLDER')"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('AUTOMATION.ACTION.CUSTOM_WEBHOOK.HEADERS') }}
      </label>
      <WootMessageEditor
        v-model="headers"
        class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-background rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
        :placeholder="headerPlaceholder"
        rows="2"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('AUTOMATION.ACTION.CUSTOM_WEBHOOK.BODY') }}
      </label>
      <WootMessageEditor
        v-model="body"
        class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-background rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
        :placeholder="bodyPlaceholder"
        enable-variables
        rows="4"
      />
      <div class="mt-1 p-2 bg-n-alpha-1 rounded-md text-xs text-n-slate-11">
        <p class="font-medium mb-1 text-n-slate-12">
          {{ t('AUTOMATION.ACTION.CUSTOM_WEBHOOK.VARIABLES') }}
        </p>
        <ul
          class="list-disc pl-4 grid grid-cols-2 gap-x-2 gap-y-1 font-mono text-[10px]"
        >
          <li v-for="variable in availableVariables" :key="variable">
            {{ variable }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
